import { useSetContainerValues } from '@/components/container/context/context';
import { SheetAlertContentLight, SheetAlertLight } from '@/components/sheet-alert/light';
import { SkeletonLight } from '@/components/skeleton/skeleton.light';
import { env } from '@/env';
import { formatPhone } from '@/lib';
import { type UsernameAlias } from '@/portal-api.definition';
import { Apple, ChevronRight, FacebookColored, GoogleColored, Loader } from '@nosinovacao/fe-icons';
import {
  ActionCard,
  Button,
  cn,
  ExternalLoginProviders,
  useI18n,
} from '@nosinovacao/nosid-mfe-common';
import { Link, Outlet } from '@tanstack/react-router';
import { useState, type ComponentType } from 'react';
import { type HomePageProps } from '../-model/props';

export function HomePageLight({
  isLoading,
  socialNetworks,
  usernameAliases,
  associateSocialNetwork,
  associateSocialNetworkLoading,
  removeSocialNetwork,
  removeSocialNetworkLoading,
}: Readonly<HomePageProps>) {
  const { t } = useI18n();
  useSetContainerValues({
    title: t('Home.Title'),
  });

  const externalLoginProvidersCount = Object.entries(env.ExternalLoginProviders).filter(
    ([, value]) => Boolean(value),
  ).length;

  return (
    <>
      <main className="grid gap-2 desktop-md:grid-cols-[1fr_minmax(21.25rem,max-content)] desktop-md:gap-x-6 desktop-md:gap-y-4">
        <SkeletonLight loading={isLoading} className="h-48 w-full">
          <UsernameAliasCard aliases={usernameAliases} />
        </SkeletonLight>
        <div className="flex flex-col gap-2">
          <SkeletonLight loading={isLoading} className="h-[92px] w-full">
            <ChangeUsernameAliasesCard />
          </SkeletonLight>
          <SkeletonLight loading={isLoading} className="h-[92px] w-full">
            <ChangePasswordCard />
          </SkeletonLight>
        </div>
        <div className="max-desktop-md:mt-4">
          {externalLoginProvidersCount ? (
            <SkeletonLight
              loading={isLoading}
              className={cn(
                'w-full',
                externalLoginProvidersCount === 1 && 'h-[9.5rem]',
                externalLoginProvidersCount === 2 && 'h-52',
                externalLoginProvidersCount === 3 && 'h-[16.5rem]',
              )}
            >
              <SocialNetworksCard
                activeSocialNetworks={socialNetworks}
                associateSocialNetworkLoading={associateSocialNetworkLoading}
                associateSocialNetwork={associateSocialNetwork}
                removeSocialNetwork={removeSocialNetwork}
                removeSocialNetworkLoading={removeSocialNetworkLoading}
              />
            </SkeletonLight>
          ) : null}
        </div>
      </main>
      <Outlet />
    </>
  );
}

function UsernameAliasCard({ aliases: usernameAliases }: Readonly<{ aliases: UsernameAlias[] }>) {
  const { t } = useI18n();
  const emailAlias = usernameAliases.find((al) => al.Type === 'Email');
  const mobileAlias = usernameAliases.find((al) => al.Type === 'Mobile');
  const otherAlias = usernameAliases.find((al) => al.Type === 'Other');
  const aliases: UsernameAlias[] = [
    {
      Type: 'Mobile',
      Username: mobileAlias?.Username ? formatPhone(mobileAlias.Username) : '',
    },
    {
      Type: 'Email',
      Username: emailAlias?.Username ?? '',
    },
  ];

  if (otherAlias) {
    aliases.push({
      Type: 'Other',
      Username: otherAlias.Username,
    });
  }
  return (
    <div className="grid gap-5 rounded border border-nos-neutral-100 bg-nos-neutral-0 p-4 shadow-1">
      <span
        className="font-medium text-nos-neutral-900 font-title-h4"
        data-tid="title-login-aliases"
      >
        {t('Home.LoginUsernameAliases')}
      </span>
      {aliases.map((al) => (
        <div className="grid gap-1" key={al.Type}>
          <span className="text-nos-neutral-500 font-text-p2">
            {t(`Home.UsernameAlias.${al.Type}`)}
          </span>
          <span
            className="text-nos-neutral-900 font-text-p1"
            data-tid={`text-alias-${al.Type.toLowerCase()}`}
          >
            {al.Username || t('Home.UsernameAlias.Default')}
          </span>
        </div>
      ))}
    </div>
  );
}

function ChangeUsernameAliasesCard() {
  const { t } = useI18n();
  return (
    <ActionCard
      skin="light"
      variant="quick-link"
      preTitle={t('Home.ActionChange')}
      subTitle={<></>}
      title={t('Home.LoginUsernameAliases')}
      icon={<ChevronRight width={20} height={20} />}
      className="w-full bg-nos-neutral-0 shadow-1"
      data-tid="card-change-contacts"
      asChild
    >
      <Link to="/home/change-contacts" />
    </ActionCard>
  );
}
function ChangePasswordCard() {
  const { t } = useI18n();
  return (
    <ActionCard
      skin="light"
      variant="quick-link"
      preTitle={t('Home.ActionChange')}
      subTitle={<></>}
      title={t('Home.Password')}
      icon={<ChevronRight width={20} height={20} />}
      className="w-full bg-nos-neutral-0 shadow-1"
      data-tid="card-change-password"
      asChild
    >
      <Link to="/home/change-password" />
    </ActionCard>
  );
}

const providerIcons: Record<
  ExternalLoginProviders,
  ComponentType<{ width: number; height: number }>
> = {
  Apple,
  Facebook: FacebookColored,
  Google: GoogleColored,
};

function SocialNetworksCard({
  activeSocialNetworks,
  associateSocialNetwork,
  associateSocialNetworkLoading,
  removeSocialNetwork,
  removeSocialNetworkLoading,
}: Readonly<{
  activeSocialNetworks: ExternalLoginProviders[];
  associateSocialNetworkLoading: ExternalLoginProviders | null;
  associateSocialNetwork: (provider: ExternalLoginProviders) => void;
  removeSocialNetworkLoading: boolean;
  removeSocialNetwork: (provider: ExternalLoginProviders) => Promise<void>;
}>) {
  const { t } = useI18n();
  const { Apple, Facebook, Google } = env.ExternalLoginProviders;
  const providers: Array<{ provider: ExternalLoginProviders; active: boolean }> = [];
  if (Google) {
    providers.push({
      provider: ExternalLoginProviders.Google,
      active: activeSocialNetworks.includes(ExternalLoginProviders.Google),
    });
  }
  if (Apple) {
    providers.push({
      provider: ExternalLoginProviders.Apple,
      active: activeSocialNetworks.includes(ExternalLoginProviders.Apple),
    });
  }
  if (Facebook) {
    providers.push({
      provider: ExternalLoginProviders.Facebook,
      active: activeSocialNetworks.includes(ExternalLoginProviders.Facebook),
    });
  }
  return (
    <div className="grid gap-6 rounded border border-nos-neutral-100 bg-nos-neutral-0 p-4 shadow-1">
      <div className="grid gap-2">
        <span
          className="font-medium text-nos-neutral-900 font-title-h4"
          data-tid="title-social-networks"
        >
          {t('Home.SocialNetworks.Title')}
        </span>
        <span className="font-light text-nos-neutral-900 font-text-p2">
          {t('Home.SocialNetworks.SubTitle')}
        </span>
      </div>
      <div className="grid gap-4">
        {providers.map(({ active, provider }) => {
          const Icon = providerIcons[provider];
          return (
            <div key={provider} className="flex items-center gap-1 [&>*]:shrink-0">
              {<Icon width={24} height={24} />}
              <span className="text-nos-neutral-900 font-text-p1">
                {t(`Home.SocialNetworks.Providers.${provider}`)}
              </span>
              <LoginProviderButton
                active={active}
                provider={provider}
                associateSocialNetwork={associateSocialNetwork}
                associateSocialNetworkLoading={associateSocialNetworkLoading}
                removeSocialNetwork={removeSocialNetwork}
                removeSocialNetworkLoading={removeSocialNetworkLoading}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LoginProviderButton({
  active,
  provider,
  associateSocialNetworkLoading,
  associateSocialNetwork,
  removeSocialNetwork,
  removeSocialNetworkLoading,
}: Readonly<{
  provider: ExternalLoginProviders;
  active: boolean;
  associateSocialNetworkLoading: ExternalLoginProviders | null;
  associateSocialNetwork: (provider: ExternalLoginProviders) => void;
  removeSocialNetworkLoading: boolean;
  removeSocialNetwork: (provider: ExternalLoginProviders) => Promise<void>;
}>) {
  const { t } = useI18n();
  const [alertOpen, setAlertOpen] = useState(false);
  const buttonText = t(active ? 'Home.SocialNetworks.Unlink' : 'Home.SocialNetworks.Link');
  return (
    <>
      <Button
        onClick={() => {
          if (active) {
            setAlertOpen(true);
            return;
          }
          associateSocialNetwork(provider);
        }}
        skin="light"
        disabled={associateSocialNetworkLoading !== null}
        variant="tertiary"
        type="button"
        destructive={active}
        data-tid={`button-social-${provider.toLowerCase()}`}
        className="ml-auto data-[destructive=false]:data-[pressing=true]:text-nos-blue-700 data-[destructive=false]:text-nos-blue data-[destructive=false]:hover:text-nos-blue-700 data-[destructive=false]:disabled:text-nos-neutral-200 "
      >
        {associateSocialNetworkLoading === provider ? (
          <Loader height={24} width={24} />
        ) : (
          buttonText
        )}
      </Button>
      <SheetAlertLight
        data-tid="alert-unlink-social"
        open={alertOpen && active}
        onOpenChange={(o) => {
          if (o) {
            setAlertOpen(true);
            return;
          }

          if (!removeSocialNetworkLoading) {
            setAlertOpen(false);
          }
        }}
      >
        <SheetAlertContentLight
          type="destructive"
          title={t('Home.SocialNetworks.Alert.Title')}
          description={t('Home.SocialNetworks.Alert.Description')}
          cancel={{
            children: t('Home.SocialNetworks.Alert.Cancel'),
            disabled: removeSocialNetworkLoading,
            onClick: () => setAlertOpen(false),
          }}
          confirm={{
            children: removeSocialNetworkLoading ? (
              <Loader height={24} width={24} />
            ) : (
              t('Home.SocialNetworks.Alert.Confirm')
            ),
            disabled: removeSocialNetworkLoading,
            onClick: () => {
              if (removeSocialNetworkLoading) return;
              removeSocialNetwork(provider).then(() => {
                setAlertOpen(false);
              });
            },
          }}
        />
      </SheetAlertLight>
    </>
  );
}
